import { ApiError } from '@/lib/api/error';
import { uploadLogsheetDocumentRequest } from '@/services/logsheets-maker';
import type { LogsheetDocument } from '@/types/logsheets-maker';

/**
 * Sequentially upload staged files for a freshly-created logsheet entry,
 * partitioning the per-file outcomes into ok / failed buckets.
 *
 * Used by the maker create + bulk wizards after the create POST returns
 * the new entry id(s). Per-file failures DO NOT throw — the caller wants
 * a partial-success summary rather than an early abort, because the entry
 * is already persisted and the user can retry the failed uploads from the
 * Edit page.
 *
 * Sequential (not parallel) so progress feedback per file is meaningful
 * and the backend rate-limit isn't hit by 50 simultaneous multipart POSTs
 * out of one bulk batch. The per-row count in bulk is small in practice
 * (usually 0–3 files per machine), so total wall time stays acceptable.
 *
 * REVIEW-4 FF M-2: accepts an optional `signal` that the caller can use to
 * cancel in-flight uploads if the wizard unmounts mid-batch. When the signal
 * aborts, the remaining unsent files land in `failed[]` with an explicit
 * cancellation message so the caller can show a coherent toast.
 */

export interface StagedUploadOutcome {
  readonly ok: readonly LogsheetDocument[];
  readonly failed: readonly { readonly file: File; readonly error: string }[];
}

export async function uploadStagedFiles(
  entryId: number,
  files: readonly File[],
  signal?: AbortSignal,
): Promise<StagedUploadOutcome> {
  const ok: LogsheetDocument[] = [];
  const failed: { file: File; error: string }[] = [];

  for (const file of files) {
    // REVIEW-4 FF M-2: short-circuit if cancelled — remaining files are
    // marked failed with a deterministic message so the caller's toast can
    // distinguish cancellation from a server error.
    if (signal?.aborted === true) {
      failed.push({ file, error: 'Upload cancelled.' });
      continue;
    }
    try {
      const doc = await uploadLogsheetDocumentRequest(entryId, file, undefined, signal);
      ok.push(doc);
    } catch (e) {
      // REVIEW-4 FF M-2: distinguish user-initiated abort from generic errors.
      // axios surfaces cancellation as a CanceledError whose .name === 'CanceledError'
      // (or whose code === 'ERR_CANCELED'); also check signal.aborted as a
      // belt-and-braces fallback for fetch-shaped throwers.
      // TS narrows signal?.aborted to `false | undefined` after the loop-top
      // guard, so use Boolean() to re-broaden — the value can flip to true
      // during the in-flight await.
      if (Boolean(signal?.aborted) || (e instanceof Error && e.name === 'CanceledError')) {
        failed.push({ file, error: 'Upload cancelled.' });
        continue;
      }
      const err = e instanceof ApiError ? e : null;
      const status = err?.status ?? 0;
      const message =
        status === 415
          ? 'Server rejected the file type.'
          : status === 413
            ? 'Server rejected the file (too large).'
            : (err?.detail ?? err?.message ?? 'Upload failed.');
      failed.push({ file, error: message });
    }
  }

  return { ok, failed };
}
